import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MainLayout from '../../layouts/MainLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { BackButton, Button } from '../../components/ui/Button';

const automationOptions = [
  {
    id: 'content-generator',
    icon: 'mdi:cards-outline',
    color: '#8B5CF6',
    bgColor: '#EDE9FE',
    title: 'Content Generator',
    subtitle: 'Auto Content',
    description: 'Auto-generate content from trending topics and keywords',
    textPath: '/templates/idea-to-post',
    carouselPath: '/templates/content-generator'
  },
  {
    id: 'random-post',
    icon: 'mdi:dice-multiple',
    color: '#EC4899',
    bgColor: '#FCE7F3',
    title: 'Random Post',
    subtitle: 'Auto Random',
    description: 'One-click random post generation from your story library',
    textPath: '/templates/random-post',
    carouselPath: '/templates/content-generator?source=random'
  },
  {
    id: 'idea-to-post',
    icon: 'mdi:lightbulb',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    title: 'Idea to Post',
    subtitle: 'Auto Writing',
    description: 'Transform your ideas into engaging posts automatically',
    textPath: '/templates/idea-to-post',
    carouselPath: '/templates/content-generator?source=idea'
  }
];

function AutomationHub() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [selectedOption, setSelectedOption] = useState(null);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
  };

  const handleTypeSelect = (type) => {
    if (!selectedOption) return;

    if (type === 'text') {
      navigate(selectedOption.textPath);
    } else {
      navigate(selectedOption.carouselPath);
    }
    setSelectedOption(null);
  };

  const closeModal = () => {
    setSelectedOption(null);
  };

  return (
    <MainLayout activePage="home">
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: '#FAFAFA'
      }}>
          {/* Back Button */}
          <BackButton
            onClick={() => navigate('/home')}
            className="fixed top-6 left-[280px] z-[100] shadow-sm"
          >
            Back to Home
          </BackButton>

          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '4rem 2rem'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#F3F4F6',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                marginBottom: '1rem'
              }}>
                <Icon icon="mdi:cog" width={16} style={{ color: '#6B7280' }} />
                <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Automation</span>
              </div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '0.75rem'
              }}>
                Choose Your Workflow
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: '#6B7280',
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                Set up automated content generation workflows
              </p>
            </div>

            {/* Options Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem'
            }}>
              {automationOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    border: '1px solid #E5E7EB',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = option.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: option.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon icon={option.icon} width={24} style={{ color: option.color }} />
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '11px',
                      color: option.color,
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '4px'
                    }}>
                      {option.subtitle}
                    </div>
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#111827',
                      marginBottom: '6px'
                    }}>
                      {option.title}
                    </h3>
                    <p style={{
                      fontSize: '13px',
                      color: '#6B7280',
                      lineHeight: '1.5',
                      margin: 0
                    }}>
                      {option.description}
                    </p>
                  </div>

                  {/* Arrow */}
                  <Icon
                    icon="mdi:chevron-right"
                    width={20}
                    style={{ color: '#9CA3AF', flexShrink: 0, marginTop: '14px' }}
                  />
                </div>
              ))}
            </div>

            {/* Manage Projects Link */}
            <div style={{
              marginTop: '2rem',
              padding: '1.25rem',
              background: '#F9FAFB',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Icon icon="mdi:folder-outline" width={20} style={{ color: '#6B7280' }} />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Manage Projects
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>
                    View and manage your automation projects
                  </div>
                </div>
              </div>
              <Button
                onClick={() => navigate('/projects')}
                variant="primary"
                size="sm"
                iconRight="mdi:arrow-right"
              >
                View Projects
              </Button>
            </div>
          </div>
        </div>

      {/* Type Selection Modal */}
      {selectedOption && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Modal Header */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                background: selectedOption.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Icon icon={selectedOption.icon} width={28} style={{ color: selectedOption.color }} />
              </div>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '0.5rem'
              }}>
                {selectedOption.title}
              </h2>
              <p style={{
                fontSize: '14px',
                color: '#6B7280'
              }}>
                Choose your output format
              </p>
            </div>

            {/* Type Options */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              {/* Text Post Option */}
              <button
                onClick={() => handleTypeSelect('text')}
                style={{
                  flex: 1,
                  padding: '1.25rem',
                  background: '#F9FAFB',
                  border: '2px solid #E5E7EB',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3B82F6';
                  e.currentTarget.style.background = '#EFF6FF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.background = '#F9FAFB';
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#DBEAFE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem'
                }}>
                  <Icon icon="mdi:text" width={24} style={{ color: '#3B82F6' }} />
                </div>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '0.25rem'
                }}>
                  Text Post
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#6B7280'
                }}>
                  Pure text content
                </div>
              </button>

              {/* Carousel Option */}
              <button
                onClick={() => handleTypeSelect('carousel')}
                style={{
                  flex: 1,
                  padding: '1.25rem',
                  background: '#F9FAFB',
                  border: '2px solid #E5E7EB',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#8B5CF6';
                  e.currentTarget.style.background = '#F5F3FF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.background = '#F9FAFB';
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#EDE9FE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem'
                }}>
                  <Icon icon="mdi:cards-outline" width={24} style={{ color: '#8B5CF6' }} />
                </div>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '0.25rem'
                }}>
                  Carousel Cards
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#6B7280'
                }}>
                  Image carousel
                </div>
              </button>
            </div>

            {/* Cancel Button */}
            <Button
              onClick={closeModal}
              variant="ghost"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </MainLayout>
  );
}

export default AutomationHub;
